import { useMutation } from "@apollo/react-hooks";
import { Card, Modal, Select, Stack, TextField } from "@shopify/polaris";
import React, { useCallback, useReducer } from "react";

import { MARK_AMAZON_IDEA_DONE_V2 } from "../../../graphql/mutations/amazon-idea";
import { arrInvalid, handleError, reducerFn } from "../../../helper";
import useTimeout from "../../../hooks/useTimeout";
import { useToastContext } from "../../shared/ToastContext";
import StoreSelect from "../../store/components/StoreSelect";
import { useAmazonIdeaCtx } from "../context";
import { AMAZON_IDEA_PRODUCT_STATUS_BADGE } from "../../../variable";

const PRODUCT_STATUS = Object.entries(AMAZON_IDEA_PRODUCT_STATUS_BADGE).map(
    ([value, label]) => ({ value, label }),
);

PRODUCT_STATUS.unshift({ label: "Choose status", value: "" });

export default function AmazonIdeaMarkDoneV2({ ids, open, onClose }) {
    const { setNotify, toggleToast } = useToastContext();
    const { refetch } = useAmazonIdeaCtx();
    const [state, setState] = useReducer(reducerFn, {
        storeIDs: [],
        stores: [],
    });

    const [setDelay] = useTimeout();
    const [markDone, { loading }] = useMutation(MARK_AMAZON_IDEA_DONE_V2, {
        onCompleted: () => {
            setNotify({
                msg: "Mark amazon idea Done successfully",
                err: false,
            });

            onClose();
            setDelay(() => {
                refetch && refetch();
            }, 1500);
        },
        onError: (err) => {
            setNotify({ msg: handleError(err?.toString()), err: true });
        },
    });

    const handleSubmit = useCallback(() => {
        if (arrInvalid(ids) || arrInvalid(state.storeIDs)) return;

        toggleToast && toggleToast(true);
        setNotify && setNotify({ msg: null, err: false });

        const input = [];

        for (let store of state.stores) {
            if (arrInvalid(store.amazonIdeaData)) {
                continue;
            }

            for (let amazonIdea of store.amazonIdeaData) {
                for (let amazonIdeaID of ids) {
                    input.push({
                        amazonIdeaID,
                        storeID: store.value,
                        asin: amazonIdea.asin,
                        productLink: amazonIdea.productLink,
                        status: amazonIdea.status
                            ? amazonIdea.status
                            : undefined,
                    });
                }
            }
        }

        // return;
        markDone({
            variables: {
                input,
            },
        });
    }, [ids, state.stores, state.storeIDs]);

    const handleStateChange = React.useCallback(
        (index, amazonIdeaIndex) => (value, id) => {
            const store = (state.stores || []).find((_, idx) => idx === index);

            if (store) {
                // store[id] = value;
                const amazonIdea = (store.amazonIdeaData || []).find(
                    (_, i) => i === amazonIdeaIndex,
                );
                if (amazonIdea) {
                    store.amazonIdeaData[amazonIdeaIndex] = {
                        ...store.amazonIdeaData[amazonIdeaIndex],
                        [id]: value,
                    };
                }
            }

            state.stores[index] = store;

            setState({ stores: state.stores });
        },
        [state.stores],
    );

    const handleStoreChange = useCallback((value, stores) => {
        const newStores = genStoreValues(stores);
        setState({ storeIDs: value, stores: newStores });
    }, []);

    const handleStoreRemoveAsin = useCallback(
        (storeIndex, amazonIdeaIndex) => {
            const store = state.stores.find((_, i) => i === storeIndex);
            if (!store) {
                return;
            }

            store.amazonIdeaData.splice(amazonIdeaIndex, 1);
            state.stores[storeIndex] = store;
            setState({ stores: state.stores });
        },
        [state.stores],
    );

    const handleStoreAddAsion = useCallback(
        (index) => {
            const storeValue = newStoreValue();
            const store = (state.stores || []).find((_, idx) => idx === index);
            if (!store) {
                return;
            }
            store.amazonIdeaData.push(storeValue);

            state.stores[index] = store;

            setState({ stores: state.stores });
        },
        [state.stores],
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            sectioned
            title="Mark Amazon Idea to Done"
            primaryAction={{
                content: "Submit",
                onAction: handleSubmit,
                loading: loading,
            }}
            secondaryActions={[{ content: "Cancel", onAction: onClose }]}
        >
            <Stack vertical>
                <StoreSelect
                    value={state.storeIDs}
                    filter={{ platform: "amazon", limit: 200 }}
                    onChange={handleStoreChange}
                    getValueImmediate
                    allowMultiple
                />

                {state.stores?.length > 0 ? (
                    <>
                        {state.stores.map((store, index) => (
                            <AmazonIdeaMarkDoneByStore
                                key={`store-${
                                    store?.value || store?.label || index
                                }`}
                                store={store}
                                // handleStateChange={handleStateChange(index)}
                                storeIndex={index}
                                onStateChange={handleStateChange}
                                onStoreAddAsin={handleStoreAddAsion}
                                onStoreRemoveAsin={handleStoreRemoveAsin}
                            />
                        ))}
                    </>
                ) : null}
            </Stack>
        </Modal>
    );
}

function AmazonIdeaMarkDoneByStore({
    store,
    onStateChange,
    storeIndex,
    onStoreAddAsin,
    onStoreRemoveAsin,
}) {
    return (
        <Card
            title={store?.label || store?.name || "Store unknown"}
            sectioned
            actions={[
                {
                    content: "Add More",
                    onAction: () => onStoreAddAsin(storeIndex),
                },
            ]}
        >
            {store?.amazonIdeaData?.length > 0 ? (
                <div
                    style={{
                        display: "flex",
                        rowGap: "2rem",
                        flexDirection: "column",
                    }}
                >
                    {store?.amazonIdeaData.map((item, amazonIdeaIndex) => (
                        <Card
                            title="Product"
                            sectioned
                            key={`amazon-idea-${storeIndex}-${amazonIdeaIndex}`}
                            actions={[
                                {
                                    content: "Delete",
                                    disabled: amazonIdeaIndex === 0,
                                    onAction: () =>
                                        onStoreRemoveAsin(
                                            storeIndex,
                                            amazonIdeaIndex,
                                        ),
                                },
                            ]}
                        >
                            <Stack vertical>
                                <TextField
                                    label="Asin"
                                    id="asin"
                                    value={item.asin}
                                    onChange={onStateChange(
                                        storeIndex,
                                        amazonIdeaIndex,
                                    )}
                                    placeholder="Enter asin"
                                />
                                <TextField
                                    label="Product Link"
                                    id="productLink"
                                    value={item.productLink}
                                    onChange={onStateChange(
                                        storeIndex,
                                        amazonIdeaIndex,
                                    )}
                                    placeholder="Enter product link"
                                />
                                <Select
                                    options={PRODUCT_STATUS}
                                    value={item.status}
                                    id="status"
                                    onChange={onStateChange(
                                        storeIndex,
                                        amazonIdeaIndex,
                                    )}
                                />
                            </Stack>
                        </Card>
                    ))}
                </div>
            ) : (
                <Stack vertical>
                    <TextField
                        label="Asin"
                        id="asin"
                        value={store.asin}
                        onChange={onStateChange}
                        placeholder="Enter asin"
                    />
                    <TextField
                        label="Product Link"
                        id="productLink"
                        value={store.productLink}
                        onChange={onStateChange}
                        placeholder="Enter product link"
                    />
                    <Select
                        options={PRODUCT_STATUS}
                        value={store.status || []}
                        id="status"
                        onChange={onStateChange}
                    />
                </Stack>
            )}
        </Card>
    );
}

// Utils
function genStoreValues(stores) {
    if (arrInvalid(stores)) return stores;

    const res = [];
    for (let store of stores) {
        const amazonIdeaData = newStoreValue();
        store.amazonIdeaData = [amazonIdeaData];
        res.push(store);
    }

    return res;
}

function newStoreValue() {
    return { asin: "", productLink: "", status: "" };
}
